import React, { useState, useEffect } from 'react';
import { Award, Plus, Edit, Trash2, Search, RefreshCw, CheckCircle, AlertTriangle, XCircle, Eye, User, FileText, Archive, ArchiveRestore } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/services/api';
import { Link } from 'react-router-dom';
import DocumentUpload from '@/components/DocumentUpload';
import { useApp } from '@/context/AppContext';

const getStatutBadge = (dateFin) => {
  if (!dateFin) return <Badge variant="secondary">Inconnu</Badge>;
  
  const today = new Date();
  const fin = new Date(dateFin);
  const diffDays = Math.ceil((fin - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) {
    return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Expiré</Badge>;
  } else if (diffDays <= 60) {
    return <Badge className="bg-orange-100 text-orange-800"><AlertTriangle className="h-3 w-3 mr-1" />Expire bientôt</Badge>;
  } else {
    return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Valide</Badge>;
  }
};

export default function CACES() {
  const { currentUser } = useApp();
  const [cacesList, setCacesList] = useState([]);
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categorieFilter, setCategorieFilter] = useState('all');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedCaces, setSelectedCaces] = useState(null);
  const [showArchives, setShowArchives] = useState(false);
  const [formData, setFormData] = useState({
    collaborateur_id: '',
    categorie_id: '',
    date_obtention: '',
    date_fin: '',
    organisme: '',
    numero_certificat: ''
  });

  useEffect(() => {
    loadData();
  }, [showArchives]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [cacesData, collabsData, catsData] = await Promise.all([
        api.getAllCACES(currentUser?.role, showArchives),
        api.getCollaborateurs(),
        api.getCategoriesCACES()
      ]);
      setCacesList(cacesData || []);
      setCollaborateurs(collabsData || []);
      setCategories(catsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.message?.includes('403') || error.message?.includes('non autorisé')) {
        toast.error('Accès non autorisé aux documents CACES');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setSelectedCaces(null);
    const today = new Date().toISOString().split('T')[0];
    // Calcul automatique de la date de fin (60 mois par défaut)
    const dateFin = new Date();
    dateFin.setMonth(dateFin.getMonth() + 60);
    
    setFormData({
      collaborateur_id: '',
      categorie_id: '',
      date_obtention: today,
      date_fin: dateFin.toISOString().split('T')[0],
      organisme: '',
      numero_certificat: ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.collaborateur_id || !formData.categorie_id || !formData.date_obtention || !formData.date_fin) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    // Vérifier le justificatif obligatoire pour la création
    if (!selectedCaces && !formData.document) {
      toast.error('Le justificatif est obligatoire');
      return;
    }

    try {
      let cacesId;
      
      if (selectedCaces) {
        // Mise à jour
        await api.updateCACES(selectedCaces.id, {
          categorie_id: formData.categorie_id,
          date_obtention: formData.date_obtention,
          date_fin: formData.date_fin,
          organisme: formData.organisme,
          numero_certificat: formData.numero_certificat
        });
        cacesId = selectedCaces.id;
        toast.success('CACES mis à jour avec succès');
      } else {
        // Création
        const result = await api.addCACES(formData.collaborateur_id, {
          categorie_id: formData.categorie_id,
          date_obtention: formData.date_obtention,
          date_fin: formData.date_fin,
          organisme: formData.organisme,
          numero_certificat: formData.numero_certificat
        });
        cacesId = result.id;
        toast.success('CACES enregistré avec succès');
      }
      
      // Upload du document si fourni
      if (formData.document && cacesId) {
        try {
          await api.uploadDocument(formData.document, 'caces', cacesId);
          toast.success('Document joint avec succès');
        } catch (uploadError) {
          console.error('Error uploading document:', uploadError);
          toast.error('Erreur lors de l\'upload du justificatif');
          return;
        }
      }
      
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving CACES:', error);
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedCaces) return;
    
    try {
      await api.deleteCACES(selectedCaces.id);
      toast.success('CACES supprimé');
      setDeleteDialogOpen(false);
      setSelectedCaces(null);
      loadData();
    } catch (error) {
      console.error('Error deleting CACES:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatus = (dateFin) => {
    if (!dateFin) return 'unknown';
    const today = new Date();
    const fin = new Date(dateFin);
    const diffDays = Math.ceil((fin - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'expired';
    if (diffDays <= 60) return 'expiring';
    return 'valid';
  };

  const filteredCaces = cacesList.filter(caces => {
    const matchesSearch = 
      (caces.collaborateur_nom?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (caces.collaborateur_matricule?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (caces.categorie_code?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (caces.numero_certificat?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (caces.organisme?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const status = getStatus(caces.date_fin);
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'valid' && status === 'valid') ||
      (statusFilter === 'expiring' && status === 'expiring') ||
      (statusFilter === 'expired' && status === 'expired');
    
    const matchesCategorie = categorieFilter === 'all' || caces.categorie_id === categorieFilter;
    
    return matchesSearch && matchesStatus && matchesCategorie;
  });

  // Statistics - only count non-archived items for stats
  const activeCaces = cacesList.filter(c => !c.is_archived);
  const stats = {
    total: activeCaces.length,
    valid: activeCaces.filter(c => getStatus(c.date_fin) === 'valid').length,
    expiring: activeCaces.filter(c => getStatus(c.date_fin) === 'expiring').length,
    expired: activeCaces.filter(c => getStatus(c.date_fin) === 'expired').length,
    archived: cacesList.filter(c => c.is_archived).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Award className="h-8 w-8 text-primary" />
            CACES et Habilitations
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestion des certificats CACES et habilitations des collaborateurs
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="archives"
              checked={showArchives}
              onCheckedChange={setShowArchives}
            />
            <Label htmlFor="archives" className="text-sm flex items-center gap-1 cursor-pointer">
              <Archive className="h-4 w-4" />
              Voir les archives
            </Label>
          </div>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Nouveau CACES / Habilitation
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Award className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Valides</p>
                <p className="text-2xl font-bold text-green-600">{stats.valid}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expirent bientôt</p>
                <p className="text-2xl font-bold text-orange-600">{stats.expiring}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{showArchives ? 'Archivés' : 'Expirés'}</p>
                <p className="text-2xl font-bold text-red-600">{showArchives ? stats.archived : stats.expired}</p>
              </div>
              {showArchives ? <Archive className="h-8 w-8 text-gray-500" /> : <XCircle className="h-8 w-8 text-red-500" />}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par collaborateur, matricule, catégorie, organisme..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="valid">Valides</SelectItem>
                <SelectItem value="expiring">Expirent bientôt</SelectItem>
                <SelectItem value="expired">Expirés</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categorieFilter} onValueChange={setCategorieFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.code} - {cat.libelle}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Collaborateur</TableHead>
                <TableHead>Catégorie</TableHead>
                <TableHead>N° Certificat</TableHead>
                <TableHead>Organisme</TableHead>
                <TableHead>Date obtention</TableHead>
                <TableHead>Date fin</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCaces.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <div className="text-center py-12">
                      <Award className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-gray-500">Aucun CACES / Habilitation trouvé</p>
                      <button
                        onClick={() => setDialogOpen(true)}
                        className="text-primary hover:underline text-sm mt-2 inline-block"
                      >
                        Créer un CACES / Habilitation
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCaces.map((caces) => (
                  <TableRow key={caces.id} className={caces.is_archived ? 'bg-gray-50 opacity-70' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {caces.collaborateur_nom || 'N/A'}
                            {caces.is_archived && (
                              <Badge variant="secondary" className="text-xs">
                                <Archive className="h-3 w-3 mr-1" />
                                Archivé
                              </Badge>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">{caces.collaborateur_matricule}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{caces.categorie_code}</Badge>
                    </TableCell>
                    <TableCell>{caces.numero_certificat || '-'}</TableCell>
                    <TableCell>{caces.organisme || '-'}</TableCell>
                    <TableCell>
                      {caces.date_obtention ? new Date(caces.date_obtention).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>
                      {caces.date_fin ? new Date(caces.date_fin).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell>{getStatutBadge(caces.date_fin)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Documents"
                          onClick={() => {
                            setSelectedCaces(caces);
                            setDocumentsDialogOpen(true);
                          }}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Link to={`/collaborateurs/${caces.collaborateur_id}`}>
                          <Button variant="ghost" size="icon" title="Voir collaborateur">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedCaces(caces);
                            setDeleteDialogOpen(true);
                          }}
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau CACES / Habilitation</DialogTitle>
            <DialogDescription>
              Enregistrer un nouveau certificat CACES ou une habilitation pour un collaborateur
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="collaborateur">Collaborateur *</Label>
                <Select
                  value={formData.collaborateur_id}
                  onValueChange={(value) => setFormData({ ...formData, collaborateur_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {collaborateurs.filter(c => c.actif).map(collab => (
                      <SelectItem key={collab.id} value={collab.id}>
                        {collab.prenom} {collab.nom} ({collab.matricule})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="categorie">Catégorie CACES / Habilitation *</Label>
                <Select
                  value={formData.categorie_id}
                  onValueChange={(value) => {
                    const selectedCat = categories.find(c => c.id === value);
                    let newDateFin = formData.date_fin;
                    
                    // Calcul automatique de la date de fin si date d'obtention existe
                    if (formData.date_obtention && selectedCat?.validite_mois) {
                      const dateObtention = new Date(formData.date_obtention);
                      dateObtention.setMonth(dateObtention.getMonth() + selectedCat.validite_mois);
                      newDateFin = dateObtention.toISOString().split('T')[0];
                    }
                    
                    setFormData({ ...formData, categorie_id: value, date_fin: newDateFin });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.actif !== false).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.code} - {cat.libelle} ({cat.validite_mois ? `${cat.validite_mois} mois` : '60 mois'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_obtention">Date d&apos;obtention *</Label>
                  <Input
                    id="date_obtention"
                    type="date"
                    value={formData.date_obtention}
                    onChange={(e) => {
                      const newDateObtention = e.target.value;
                      const selectedCat = categories.find(c => c.id === formData.categorie_id);
                      let newDateFin = formData.date_fin;
                      
                      // Calcul automatique de la date de fin
                      if (newDateObtention && selectedCat?.validite_mois) {
                        const dateObtention = new Date(newDateObtention);
                        dateObtention.setMonth(dateObtention.getMonth() + selectedCat.validite_mois);
                        newDateFin = dateObtention.toISOString().split('T')[0];
                      } else if (newDateObtention) {
                        // Par défaut 60 mois (5 ans) si pas de validite_mois défini
                        const dateObtention = new Date(newDateObtention);
                        dateObtention.setMonth(dateObtention.getMonth() + 60);
                        newDateFin = dateObtention.toISOString().split('T')[0];
                      }
                      
                      setFormData({ ...formData, date_obtention: newDateObtention, date_fin: newDateFin });
                    }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_fin">Date de fin *</Label>
                  <Input
                    id="date_fin"
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="organisme">Organisme de formation</Label>
                <Input
                  id="organisme"
                  value={formData.organisme}
                  onChange={(e) => setFormData({ ...formData, organisme: e.target.value })}
                  placeholder="Ex: AFTRAL, PROMOTRANS..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="numero_certificat">N° Certificat</Label>
                <Input
                  id="numero_certificat"
                  value={formData.numero_certificat}
                  onChange={(e) => setFormData({ ...formData, numero_certificat: e.target.value })}
                  placeholder="Numéro du certificat"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="document">Justificatif *</Label>
                <Input
                  id="document"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setFormData({ ...formData, document: e.target.files[0] })}
                  className="cursor-pointer"
                  required={!selectedCaces}
                />
                <p className="text-xs text-gray-500">Format accepté: PDF, JPG, PNG (obligatoire)</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce CACES / cette habilitation ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Documents CACES / Habilitation - {selectedCaces?.collaborateur_nom}
            </DialogTitle>
            <DialogDescription>
              Catégorie {selectedCaces?.categorie_code}
            </DialogDescription>
          </DialogHeader>
          {selectedCaces && (
            <DocumentUpload 
              entityType="caces" 
              entityId={selectedCaces.id}
              title="Documents du certificat CACES / Habilitation"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
